import type { InstrumentState } from '@/domain/types';
import { isRecord, readArray, readBool, readId, readInt, readNumber } from '@/domain/format/validate';
import { midiNoteName } from './descriptors';

/**
 * Sampler zones.
 *
 * A zone maps a region of an audio asset onto a range of the keyboard. Zones
 * live in `InstrumentState.data`, which the project format carries as free-form
 * structured state — so the sampler saves, loads, packages and garbage-collects
 * with no changes to the format, and `.dawproject` export already picks up the
 * referenced audio because it is registered in `project.assets` like any other.
 *
 * `data` is untyped by design, so everything here is read defensively: a
 * hand-edited or older file degrades to a usable instrument rather than
 * throwing during load.
 */

export interface SamplerZone {
  id: string;
  assetId: string;
  name: string;
  /** The pitch at which the sample plays back untransposed. */
  rootPitch: number;
  /** Inclusive key range this zone answers to. */
  lowPitch: number;
  highPitch: number;
  /** Region of the source asset, in seconds. */
  offsetSec: number;
  durationSec: number;
  gain: number;
  /** Fine tuning in cents. */
  tuneCents: number;
  /**
   * Inclusive velocity range this zone answers to, 1-127.
   *
   * A multisampled instrument is layered by force as well as by pitch: a piano
   * struck hard is a different sound, not a louder one, so it is a different
   * recording. A zone covering the whole range is the default and behaves as
   * before.
   */
  lowVelocity: number;
  highVelocity: number;
  /**
   * Zones sharing a group take turns rather than sounding together.
   *
   * Two takes of the same hit, alternating, is what stops a fast repeated note
   * sounding like a machine gun — the identical waveform played twice in a row
   * is exactly what the ear notices. Zero means "not in a group": the zone plays
   * whenever it matches.
   */
  roundRobinGroup: number;
  /** Hold the sample as long as the note is held, looping between the loop points. */
  loop: boolean;
  /**
   * Where the loop turns around, in source seconds, inside the region. The
   * region's own ends by default, which loops the whole zone. A sample with an
   * attack and then a sustain puts them further in: the attack plays once on
   * the way to the loop, the loop holds while the key is down, and the release
   * plays out from wherever the loop was through the rest of the region.
   */
  loopStartSec: number;
  loopEndSec: number;
  /**
   * How long the loop's end is blended into its start, in seconds, so the
   * join does not click. Wanted, not necessarily had: it can be no longer than
   * what leads into the loop, and no more than half the loop.
   */
  loopCrossfadeSec: number;
  /**
   * Play the region out to its end whatever the note's length: a cymbal rings
   * however short the note that struck it. The note letting go is ignored;
   * stopping the transport still stops it. Exclusive with `loop`, which would
   * never end.
   */
  oneShot: boolean;
  /**
   * Whether the pitch follows the key. On, a key is resampled by its distance
   * from the root note, which is what a multisample wants. Off, every key in
   * the range plays the sample as recorded — a snare laid across a dozen keys
   * is one snare, not twelve pitches of it. Fine tuning, the instrument's
   * transpose and the bend still apply.
   */
  keyTracking: boolean;
  /**
   * Zones sharing a choke group cut each other: the moment one starts, every
   * voice the group has sounding is stopped. A closed hi-hat silences the
   * open one because on a kit they are the same cymbal. Zero means "in no
   * group"; a zone chokes its own earlier voices too, as a hi-hat does.
   */
  chokeGroup: number;
  reverse: boolean;
}

/** The crossfade a new zone gets: short enough to be invisible on a sustain, long enough to hide a step. */
export const DEFAULT_LOOP_CROSSFADE_SEC = 0.01;
export const MAX_LOOP_CROSSFADE_SEC = 1;

/** The shortest loop worth turning around in. */
export const MIN_LOOP_SEC = 0.001;

/**
 * Loop points that make sense for a zone: inside its region, in order, and at
 * least a millisecond apart — or the whole region when they are not.
 *
 * Given the zone as it was, a point that sat on a region edge follows that edge
 * when the edge moves, so a zone that loops "the whole region" keeps doing so
 * after a trim; a point set inside the region stays where it was put.
 */
export function repairLoopPoints(
  zone: Pick<SamplerZone, 'offsetSec' | 'durationSec' | 'loopStartSec' | 'loopEndSec'>,
  previous?: Pick<SamplerZone, 'offsetSec' | 'durationSec' | 'loopStartSec' | 'loopEndSec'>,
): { loopStartSec: number; loopEndSec: number } {
  const regionEnd = zone.offsetSec + zone.durationSec;
  let start = zone.loopStartSec;
  let end = zone.loopEndSec;
  if (previous) {
    if (previous.loopStartSec === previous.offsetSec && start === previous.loopStartSec) start = zone.offsetSec;
    const previousEnd = previous.offsetSec + previous.durationSec;
    if (previous.loopEndSec === previousEnd && end === previous.loopEndSec) end = regionEnd;
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { loopStartSec: zone.offsetSec, loopEndSec: regionEnd };
  start = Math.min(regionEnd, Math.max(zone.offsetSec, start));
  end = Math.min(regionEnd, Math.max(zone.offsetSec, end));
  if (end - start < MIN_LOOP_SEC) return { loopStartSec: zone.offsetSec, loopEndSec: regionEnd };
  return { loopStartSec: start, loopEndSec: end };
}

export const MAX_ZONES = 128;

export function readSamplerZones(data: Record<string, unknown> | undefined): SamplerZone[] {
  const raw = readArray(data?.zones).slice(0, MAX_ZONES);
  const zones: SamplerZone[] = [];

  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const id = readId(entry.id);
    const assetId = readId(entry.assetId);
    if (!id || !assetId) continue;

    const rootPitch = readInt(entry.rootPitch, 60, 0, 127);
    const low = readInt(entry.lowPitch, 0, 0, 127);
    const high = readInt(entry.highPitch, 127, 0, 127);

    const offsetSec = readNumber(entry.offsetSec, 0, 0, 24 * 3600);
    const durationSec = readNumber(entry.durationSec, 0, 0, 24 * 3600);
    zones.push({
      id,
      assetId,
      name: typeof entry.name === 'string' ? entry.name.slice(0, 128) : 'Sample',
      rootPitch,
      // An inverted range would answer to nothing; it is repaired rather than dropped.
      lowPitch: Math.min(low, high),
      highPitch: Math.max(low, high),
      offsetSec,
      durationSec,
      // Repaired rather than dropped, exactly as the key range is: an inverted
      // range would answer to nothing, which is never what was meant.
      lowVelocity: Math.min(readInt(entry.lowVelocity, 1, 1, 127), readInt(entry.highVelocity, 127, 1, 127)),
      highVelocity: Math.max(readInt(entry.lowVelocity, 1, 1, 127), readInt(entry.highVelocity, 127, 1, 127)),
      roundRobinGroup: readInt(entry.roundRobinGroup, 0, 0, 64),
      chokeGroup: readInt(entry.chokeGroup, 0, 0, 64),
      gain: readNumber(entry.gain, 1, 0, 4),
      tuneCents: readNumber(entry.tuneCents, 0, -1200, 1200),
      loop: readBool(entry.loop, false),
      // A file that says both is read as looping; the command never writes both.
      oneShot: readBool(entry.oneShot, false) && !readBool(entry.loop, false),
      keyTracking: readBool(entry.keyTracking, true),
      // Absent in an older file, which looped the whole region; read as that,
      // with the crossfade every new zone gets.
      loopCrossfadeSec: readNumber(entry.loopCrossfadeSec, DEFAULT_LOOP_CROSSFADE_SEC, 0, MAX_LOOP_CROSSFADE_SEC),
      ...repairLoopPoints({
        offsetSec,
        durationSec,
        loopStartSec: readNumber(entry.loopStartSec, offsetSec, 0, 24 * 3600),
        loopEndSec: readNumber(entry.loopEndSec, offsetSec + durationSec, 0, 24 * 3600),
      }),
      reverse: readBool(entry.reverse, false),
    });
  }

  return zones;
}

export function writeSamplerZones(state: InstrumentState, zones: readonly SamplerZone[]): InstrumentState {
  return { ...state, data: { ...state.data, zones: zones.slice(0, MAX_ZONES).map((zone) => ({ ...zone })) } };
}

/** Zones that answer to a pitch, in the order they were added. */
export function zonesForPitch(zones: readonly SamplerZone[], pitch: number): SamplerZone[] {
  return zones.filter((zone) => pitch >= zone.lowPitch && pitch <= zone.highPitch);
}

/** Zones that answer to a pitch *and* a velocity. */
export function zonesForNote(
  zones: readonly SamplerZone[],
  pitch: number,
  velocity: number,
): SamplerZone[] {
  const force = Math.min(127, Math.max(1, Math.round(velocity)));
  return zonesForPitch(zones, pitch).filter(
    (zone) => force >= Math.min(zone.lowVelocity, zone.highVelocity) &&
      force <= Math.max(zone.lowVelocity, zone.highVelocity),
  );
}

/**
 * Chooses which of the matching zones actually sound.
 *
 * Zones outside any group all play, because layering a body and a click is a
 * normal way to build a sound. Within a group only one plays, and which one
 * advances each time the group is asked — that is the round robin. The counter
 * lives with the caller rather than in the zone, so the same instrument state
 * can be rendered offline and played live without one moving the other's turn.
 */
export function chooseZones(
  matches: readonly SamplerZone[],
  turns: Map<number, number>,
): SamplerZone[] {
  const groups = new Map<number, SamplerZone[]>();
  const out: SamplerZone[] = [];

  for (const zone of matches) {
    if (zone.roundRobinGroup <= 0) {
      out.push(zone);
      continue;
    }
    const list = groups.get(zone.roundRobinGroup);
    if (list) list.push(zone);
    else groups.set(zone.roundRobinGroup, [zone]);
  }

  for (const [group, list] of groups) {
    const turn = turns.get(group) ?? 0;
    out.push(list[turn % list.length]);
    turns.set(group, (turn + 1) % list.length);
  }
  return out;
}

/**
 * Recomputes key ranges so every zone covers the keys nearest its root note.
 *
 * A multisample is unplayable until its zones span the keyboard, and asking
 * someone to type sixty range boundaries by hand is not a feature. Splitting
 * each gap at its midpoint is what a sampler is expected to do, and the ranges
 * remain editable afterwards.
 */
export function autoAssignRanges(zones: readonly SamplerZone[]): SamplerZone[] {
  if (zones.length === 0) return [];
  const sorted = [...zones].sort((a, b) => a.rootPitch - b.rootPitch);

  return sorted.map((zone, index) => {
    const previous = sorted[index - 1];
    const next = sorted[index + 1];
    const low = previous ? Math.floor((previous.rootPitch + zone.rootPitch) / 2) + 1 : 0;
    const high = next ? Math.floor((zone.rootPitch + next.rootPitch) / 2) : 127;
    return { ...zone, lowPitch: Math.max(0, Math.min(low, zone.rootPitch)), highPitch: Math.min(127, Math.max(high, zone.rootPitch)) };
  });
}

const NOTE_OFFSETS: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

/**
 * Finds a note name in a file name — `Piano_C3.wav`, `strings-f#2-vl3.aif`.
 *
 * Sample libraries name files this way almost universally, and reading the
 * root note off the name is the difference between dropping in a folder and
 * having a playable instrument, versus dropping in a folder and then setting
 * sixty root notes by hand. When no name is found the caller falls back to
 * mapping files in order.
 */
export function detectRootPitch(fileName: string): number | null {
  // Matches a note letter, optional accidental, and an octave from -1 to 9,
  // anchored to a separator so `A4` in `TAKE4` is not mistaken for a note.
  const match = /(?:^|[^a-z0-9])([a-g])([#sb]?)(-?[0-9])(?![0-9])/i.exec(fileName);
  if (!match) return null;

  const [, letter, accidental, octave] = match;
  const base = NOTE_OFFSETS[letter.toLowerCase()];
  if (base === undefined) return null;

  const shift = accidental === '#' || accidental.toLowerCase() === 's' ? 1 : accidental.toLowerCase() === 'b' ? -1 : 0;
  // Middle C is C4, matching the note names shown in the piano roll.
  const pitch = (Number(octave) + 1) * 12 + base + shift;
  return pitch >= 0 && pitch <= 127 ? pitch : null;
}

/**
 * Chooses root notes for a set of dropped files: names win where they contain a
 * note, and anything else is laid out in order from C3 upwards.
 */
export function assignRootPitches(fileNames: readonly string[], startPitch = 48): number[] {
  const detected = fileNames.map(detectRootPitch);
  if (detected.every((pitch) => pitch !== null)) return detected as number[];

  let next = startPitch;
  return detected.map((pitch) => {
    if (pitch !== null) return pitch;
    const assigned = Math.min(127, next);
    next += 1;
    return assigned;
  });
}

/** `C3 · C2–C4` style summary shown next to a zone. */
export function describeZoneRange(zone: SamplerZone): string {
  const root = midiNoteName(zone.rootPitch);
  if (zone.lowPitch === zone.highPitch) return `${root} · ${midiNoteName(zone.lowPitch)} only`;
  return `${root} · ${midiNoteName(zone.lowPitch)}–${midiNoteName(zone.highPitch)}`;
}
